import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, AlertCircle, Calendar, MapPin, Truck, Package, CreditCard, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND_ICONS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
};

const Paiement = () => {
  const { devisId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [devis, setDevis] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [payingWithSaved, setPayingWithSaved] = useState(false);

  useEffect(() => {
    fetchDevis();
    if (user && token) {
      fetchSavedCards();
    }
  }, [devisId, user, token]);

  const fetchDevis = async () => {
    setError(null);
    setLoading(true);
    try {
      const [response, priceResponse] = await Promise.all([axios.get(`${API}/devis/${devisId}`), axios.get(`${API}/devis/${devisId}/pricing`)]);
      setPricing(priceResponse.data);
      setDevis(response.data);
      setLoading(false);
    } catch (err) {
      setError("Devis non trouvé ou invalide");
      setLoading(false);
    }
  };

  const fetchSavedCards = async () => {
    setLoadingCards(true);
    try {
      const res = await axios.get(`${API}/user/payment-methods?token=${token}`);
      setSavedCards(res.data.payment_methods || []);
    } catch (err) {
      console.error('Error fetching saved cards:', err);
    }
    setLoadingCards(false);
  };

  const handlePayment = async () => {
    setIsPaying(true);
    try {
      const origin = window.location.origin;
      let url = `${API}/payments/checkout?devis_id=${devisId}&origin_url=${encodeURIComponent(origin)}`;
      if (saveCard && token) {
        url += `&save_card=true&token=${token}`;
      } else if (token) {
        url += `&token=${encodeURIComponent(token)}`;
      }
      const res = await axios.post(url);
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Error:', error);
      alert("Une erreur est survenue. Veuillez réessayer.");
      setIsPaying(false);
    }
  };

  const handlePayWithSavedCard = async () => {
    if (!selectedCardId) return;
    setPayingWithSaved(true);
    try {
      const res = await axios.post(
        `${API}/payments/charge-saved?devis_id=${devisId}&payment_method_id=${selectedCardId}&token=${token}`
      );
      if (res.data.status === 'paid') {
        navigate(`/paiement/success?session_id=${res.data.payment_intent_id}`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur de paiement";
      alert(msg);
      setPayingWithSaved(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error || !devis) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Devis introuvable</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-green-600 hover:bg-green-700">
            Retour à l'accueil
          </Button>
        </Card>
      </div>
    );
  }

  const euros = value => Number(value).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  const price = pricing?.on_quote ? 'Prix sur devis' : `${euros(pricing.ht)} HT`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-lg">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Validation du Devis</h1>
          </div>
          <p className="text-gray-600">Location Benne Chantier - Location de Bennes</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Truck className="h-5 w-5 mr-2 text-green-600" />
                Détails de la Commande
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600">Produit</span>
                  <span className="font-semibold">1 x Benne : {devis.prestation || 'Dépôt'} {devis.benne_volume} - {devis.benne_type}</span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600">Date de prestation</span>
                  <span className="font-semibold">
                    {devis.date_debut ? new Date(devis.date_debut).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                    {devis.creneau_horaire ? ` — ${devis.creneau_horaire}` : ''}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600">Adresse de livraison</span>
                  <span className="font-semibold text-right max-w-[60%]">{devis.adresse_chantier}</span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600">Type Accès / Benne</span>
                  <span className="font-semibold">
                    {devis.type_acces === 'publique' ? 'Voie publique' : devis.type_acces === 'privee' ? 'Voie privée' : '—'}
                    {' / '}
                    {devis.type_benne_equip === 'chaine' ? 'À chaîne' : devis.type_benne_equip === 'ampiroll' ? 'Ampiroll' : devis.type_benne_equip === 'indifferent' ? 'Sans préférence' : '—'}
                  </span>
                </div>
                {(devis.contact_chantier || devis.tel_contact_chantier) && (
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Contact chantier</span>
                    <span className="font-semibold">{devis.contact_chantier || ''}{devis.tel_contact_chantier ? ` — ${devis.tel_contact_chantier}` : ''}</span>
                  </div>
                )}
                {devis.commentaire && (
                  <div className="py-3 border-b">
                    <span className="text-gray-600">Commentaire</span>
                    <p className="font-semibold text-sm mt-1">{devis.commentaire}</p>
                  </div>
                )}
              </div>
              <div className="bg-green-50 p-3 rounded-lg mt-5">
                <div className="flex items-start space-x-2">
                  <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Livraison en 24h</p>
                    <p className="text-xs text-green-700">Location de 15 jours incluse</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Package className="h-5 w-5 mr-2 text-green-600" />
                Forfait Tout Compris
              </h2>
              <ul className="space-y-2">
                {['Location de la benne pendant 15 jours', 'Transport aller-retour', 'Traitement des déchets', 'Assistance téléphonique'].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <div className="md:col-span-1">
            <Card className="p-6 sticky top-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Récapitulatif</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Benne {devis.benne_volume}</span>
                  <span>{price}</span>
                </div>
                {!pricing.on_quote && <div className="flex justify-between text-gray-600"><span>TVA (20 %)</span><span>{euros(pricing.vat)}</span></div>}
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Total TTC à payer</span>
                    <span className="text-2xl font-bold text-green-600">{pricing.on_quote ? price : euros(pricing.ttc)}</span>
                  </div>
                </div>
              </div>

              {/* Saved Cards Section */}
              {user && savedCards.length > 0 && (
                <div className="mb-5" data-testid="saved-cards-section">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Vos cartes enregistrées</p>
                  <div className="space-y-2">
                    {savedCards.map((card) => (
                      <label
                        key={card.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedCardId === card.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`saved-card-${card.last4}`}
                      >
                        <input
                          type="radio"
                          name="saved-card"
                          checked={selectedCardId === card.id}
                          onChange={() => setSelectedCardId(card.id)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500"
                        />
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">
                            {BRAND_ICONS[card.brand] || card.brand} •••• {card.last4}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                          </span>
                        </div>
                      </label>
                    ))}
                    <label
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedCardId === null ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="new-card-option"
                    >
                      <input
                        type="radio"
                        name="saved-card"
                        checked={selectedCardId === null}
                        onChange={() => setSelectedCardId(null)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500"
                      />
                      <CreditCard className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Nouvelle carte</span>
                    </label>
                  </div>

                  {selectedCardId && (
                    <Button
                      onClick={handlePayWithSavedCard}
                      disabled={payingWithSaved}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg mt-3"
                      data-testid="pay-saved-card-btn"
                    >
                      {payingWithSaved ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Traitement...
                        </>
                      ) : (
                        <>
                          <CreditCard className="mr-2 h-5 w-5" />
                          Payer avec cette carte
                        </>
                      )}
                    </Button>
                  )}

                  {selectedCardId && <div className="border-t my-4" />}
                </div>
              )}

              {/* New card payment */}
              {!selectedCardId && (
                <>
                  <Button 
                    onClick={handlePayment}
                    disabled={isPaying}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg mb-4 disabled:opacity-50"
                    data-testid="pay-btn"
                  >
                    {isPaying ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Traitement...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-5 w-5" />
                        Payer par carte
                      </>
                    )}
                  </Button>

                  {user && (
                    <label className="flex items-center gap-2 mb-4 cursor-pointer select-none" data-testid="save-card-checkbox">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">Garder la carte bancaire en mémoire</span>
                    </label>
                  )}
                </>
              )}

              <p className="text-xs text-gray-500 text-center">
                Paiement sécurisé - Vos données sont protégées
              </p>

              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 mb-2"><strong>Contact :</strong></p>
                <p className="text-sm text-gray-600">
                  07 45 55 88 35<br />
                  contact@locationbennechantier.com
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paiement;
